import React, { useState } from 'react';
import { Edit, Plus, Minus, MapPin, ExternalLink, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Product, SupplyProductAllocation } from '../../types';
import { useInventory } from '../../context/InventoryContext';
import Badge from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { getExpirationStatus, getDaysUntilExpiration } from '../../lib/dateUtils';
import AllocationBadges from './AllocationBadges';

interface ProductCardProps {
  product: Product;
  allocations?: SupplyProductAllocation[];
}

const ProductCard: React.FC<ProductCardProps> = ({ product, allocations = [] }) => {
  const { getCategoryName, getCategoryColor, isLowStock, getProductTotalStock, getProductLocations } = useInventory();
  const categoryName = getCategoryName(product.category_id);
  const categoryColor = getCategoryColor(product.category_id);
  const lowStock = isLowStock(product);
  const totalStock = getProductTotalStock(product.id);
  const productLocations = getProductLocations(product.id);
  const expirationStatus = getExpirationStatus(product.expiration_date);
  const daysUntilExpiration = getDaysUntilExpiration(product.expiration_date);

  // Helper to get expiration badge variant and label
  const getExpirationBadge = () => {
    if (expirationStatus === 'expired') {
      return { variant: 'danger' as const, label: 'Expired' };
    }
    if (expirationStatus === 'expiring-week') {
      return { variant: 'warning' as const, label: `${daysUntilExpiration}d left` };
    }
    if (expirationStatus === 'expiring-soon') {
      return { variant: 'warning' as const, label: `${daysUntilExpiration}d left` };
    }
    return null;
  };

  const expirationBadge = getExpirationBadge();

  return (
    <Card className="h-full flex flex-col transition-all duration-200 hover:shadow-md group">
      <Link to={`/supplies/${product.id}`} className="flex-grow">
        <div className="relative aspect-[4/3] overflow-hidden rounded-t-lg">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain bg-gray-50 group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-600">No image</span>
            </div>
          )}
          <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
            {lowStock && (
              <Badge variant="danger">Low Stock</Badge>
            )}
            {expirationBadge && (
              <Badge variant={expirationBadge.variant}>
                <Calendar className="h-3 w-3 mr-1" />
                {expirationBadge.label}
              </Badge>
            )}
          </div>
        </div>
        <CardContent className="flex-1 flex flex-col p-4">
          <div className="mb-2">
            <Badge color={categoryColor}>{categoryName}</Badge>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600">
            {product.name}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2 mb-2">{product.description}</p>
          <div className="mt-auto">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-gray-500">SKU: {product.sku}</span>
              <span className="text-lg font-bold text-blue-900">${product.price.toFixed(2)}</span>
            </div>
            <div className="mb-3">
              {productLocations.length > 0 ? (
                <div className="space-y-1">
                  {productLocations.slice(0, 2).map((pl) => (
                    <div key={pl.id} className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        <span className="truncate">{pl.location?.name}</span>
                      </div>
                      <span className="font-medium">{pl.quantity}</span>
                    </div>
                  ))}
                  {productLocations.length > 2 && (
                    <div className="text-xs text-gray-600">
                      +{productLocations.length - 2} more locations
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-sm text-gray-500">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span className="truncate">{product.location || 'No location set'}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Stock:</span>
              <div className="flex items-center">
                <span className={`${lowStock ? 'text-red-600 font-bold' : 'text-gray-900'} text-lg font-bold`}>
                  {totalStock}
                </span>
                <span className="text-sm text-gray-500 ml-1">{product.unit_of_measure}</span>
              </div>
            </div>

            {allocations.length > 0 && (
              <div className="mt-3">
                <AllocationBadges allocations={allocations} maxVisible={2} compact />
              </div>
            )}
          </div>
        </CardContent>
      </Link>
      <div className="px-4 pb-4">
        <Link to={`/supplies/${product.id}`} className="flex-1">
          <Button variant="secondary" fullWidth>
            <ExternalLink className="h-4 w-4 mr-1" />
            View Details
          </Button>
        </Link>
      </div>
    </Card>
  );
};

export default ProductCard;